<template>
    <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
            <div class="px-4 sm:px-0">
                <h3 class="text-lg font-medium leading-6 text-gray-900">Basic Board information</h3>
                <p class="mt-1 text-sm leading-5 text-gray-600">
                    This information will be part of your public job board.
                </p>
            </div>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
            <form @submit.prevent="submit">
                <div class="shadow sm:rounded-md sm:overflow-hidden">
                    <div class="px-4 py-5 bg-white sm:p-6">

                        <div class="">
                            <label for="name" class="block text-sm leading-5 font-medium text-gray-700">
                                Board name
                            </label>
                            <div class="mt-1">
                                <InputText id="name" v-model="form.name" />
                            </div>
                            <InputError :message="form.error('name')" class="mt-2" />
                        </div>

                        <div class="mt-6 grid grid-cols-3 gap-6">
                            <div class="col-span-3 sm:col-span-2">
                                <label for="slug"
                                       class="block text-sm font-medium leading-5 text-gray-700">
                                    Board sub-domain
                                </label>
                                <div class="mt-1 flex rounded-md shadow-sm">
                                    <input type="text" id="slug" v-model="board.slug" disabled
                                           class="form-input flex-1 block w-full rounded-none rounded-l-md transition duration-150 ease-in-out sm:text-sm sm:leading-5 bg-gray-50 text-gray-500 border-gray-300"
                                           placeholder="name-of-your-site"/>
                                    <span
                                        class="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">.jobroll.co</span>
                                </div>
                            </div>
                        </div>

                        <div class="mt-6 grid grid-cols-3 gap-6">
                            <div class="col-span-3">
                                <label for="short_description"
                                       class="block text-sm leading-5 font-medium text-gray-700">
                                    One line description
                                </label>
                                <div class="mt-1">
                                    <InputText id="short_description" v-model="form.short_description" />
                                </div>
                                <InputError :message="form.error('short_description')" class="mt-2" />
                            </div>
                        </div>

                        <div class="mt-6">
                            <label for="description" class="block text-sm leading-5 font-medium text-gray-700">
                                About your job board
                            </label>
                            <div class="mt-1">
                                        <textarea id="description" rows="3" v-model="form.description"
                                                  class="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"/>
                            </div>
                            <InputError :message="form.error('description')" class="mt-2" />
                        </div>

                        <!-- Language -->
                        <div class="mt-6 grid grid-cols-3 gap-6">
                            <div class="col-span-1">
                                <label for="lang" class="block text-sm font-medium text-gray-700">Language</label>
                                <select v-model="form.lang" id="lang" name="lang" class="mt-1 block w-full pl-3 pr-10
                                py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500
                                focus:border-indigo-500 sm:text-sm rounded-md">
                                    <option value="en">English</option>
                                    <option value="es">Spanish</option>
                                    <option value="sv">Swedish</option>
                                    <option value="sr">Serbian</option>
                                </select>
                                <InputError :message="form.error('lang')" class="mt-2" />
                            </div>
                        </div>

                        <!-- Post expiration -->
                        <div class="mt-6 grid grid-cols-3 gap-6">
                            <div class="col-span-1">
                                <label for="post_expiration"
                                       class="block text-sm leading-5 font-medium text-gray-700">
                                    How many days before job post stops being valid?
                                </label>
                                <div class="mt-1">
                                    <InputText id="post_expiration" v-model="form.post_expiration" />
                                </div>
                                <InputError :message="form.error('post_expiration')" class="mt-2" />
                            </div>
                        </div>

                        <!-- Show expired posts  -->
                        <div class="mt-6 flex items-start">
                            <div class="flex items-center h-5">
                                <input id="show-expired" v-model="form.show_expired_posts" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
                            </div>
                            <div class="ml-3 text-sm">
                                <label for="show-expired" class="font-medium text-gray-700">Show posts that are no longer valid on the home page</label>
                            </div>
                        </div>

                        <!-- Email Confirmation -->
                        <div class="mt-6 flex items-start">
                            <div class="flex items-center h-5">
                                <input id="post-confirmation" v-model="form.post_confirmation" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
                            </div>
                            <div class="ml-3 text-sm">
                                <label for="post-confirmation" class="font-medium text-gray-700">Job posting requires e-mail confirmation</label>
                            </div>
                        </div>

                        <!-- Board Logo -->
                        <div class="mt-6 col-span-6 sm:col-span-4">
                            <!-- Board Logo File Input -->
                            <input type="file" class="hidden"
                                   ref="image"
                                   @change="updateLogoPreview">

                            <Label for="logo" value="Board Logo" />

                            <!-- Current Board Logo -->
                            <div class="mt-2" v-show="! logoPreview">
                                <img :src="board.logo_local" alt="Current Board Logo" class="rounded-md h-16 object-cover">
                            </div>

                            <!-- New Board Logo Preview -->
                            <div class="mt-2" v-show="logoPreview">
                                <span class="block rounded-md w-16 h-16"
                                    :style="'background-size: cover; background-repeat: no-repeat; background-position: center center; background-image: url(\'' + logoPreview + '\');'">
                                </span>
                            </div>

                            <SecondaryButton class="mt-2 mr-2" type="button" @click.native.prevent="selectNewLogo">
                                Select A New Logo
                            </SecondaryButton>

                            <SecondaryButton type="button" class="mt-2" @click.native.prevent="deletePhoto" v-if="board.logo">
                                Remove Logo
                            </SecondaryButton>

                            <p class="mt-2 text-sm text-gray-500">Minimum width 200px. Maximum 4MB in file size.</p>
                            <InputError :message="form.error('logo')" class="mt-2" />
                        </div>
                    </div>
                    <div class="flex items-center justify-end px-4 py-3 bg-gray-50 text-right sm:px-6">
                        <ActionMessage :on="form.recentlySuccessful" class="mr-3">
                            Saved.
                        </ActionMessage>

                        <Button :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                            Save
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
    import Button from "@/Jetstream/Button";
    import ActionMessage from "@/Jetstream/ActionMessage";
    import InputError from "@/Jetstream/InputError";
    import SecondaryButton from "@/Jetstream/SecondaryButton";
    import Label from "@/Jetstream/Label";
    import InputText from "@/Jobroll/InputText";
    import Input from "@/Jetstream/Input";


    export default {

        name: "EditBasic",
        components: {
            Input,
            Button,
            ActionMessage,
            InputError,
            InputText,
            SecondaryButton,
            Label
        },
        data() {
            return {
                form: this.$inertia.form({
                    name: this.board.name,
                    slug: this.board.slug,
                    description: this.board.description,
                    short_description: this.board.short_description,
                    post_confirmation: this.board.post_confirmation,
                    show_expired_posts: this.board.show_expired_posts,
                    post_expiration: this.board.post_expiration,
                    lang: this.board.lang,
                    logo: null
                }, {
                    resetOnSuccess: false
                }),
                logoPreview: null,
            }
        },
        props: {
            board: Object,
            errors: Object
        },
        methods: {
            submit() {
                let data = new FormData();
                data.append('name', this.form.name || '');
                data.append('description', this.form.description || '');
                data.append('short_description', this.form.short_description || '');
                data.append('post_confirmation', this.form.post_confirmation);
                data.append('post_expiration', this.form.post_expiration);
                data.append('show_expired_posts', this.form.show_expired_posts);
                data.append('logo', this.form.logo || '');

                this.form.post('/settings/basic', { preserveScroll: true });
            },
            selectNewLogo() {
                this.$refs.image.click();
            },

            updateLogoPreview() {
                const reader = new FileReader();

                reader.onload = (e) => {
                    this.logoPreview = e.target.result;
                };

                this.form.logo = this.$refs.image.files[0];
                reader.readAsDataURL(this.$refs.image.files[0]);
            },

            deletePhoto() {
                this.$inertia.delete(route('publisher.settings.logo.destroy'), {
                    preserveScroll: true,
                }).then(() => {
                    this.logoPreview = this.board.logo_fallback
                });
            },
        }
    }
</script>
